/**
 * TourOverlay — reusable first-visit tour with spotlight effect.
 *
 * Lifted out of QueueTourOverlay (iter145d) so every heavy-feature
 * admin page can wire a 3-5 step on-ramp tour with ~20 lines of
 * integration: just import, drop in, pass `steps` + `storageKey`.
 *
 * Steps schema:
 *   {
 *     id:       "filters",                 // unique within the tour
 *     selector: '[data-testid="..."]',     // null/missing → centered modal
 *     placement:"below"|"above"|"right"|"below-end"|"center",
 *     title:    "Short header",
 *     body:     "1-2 sentence explanation",
 *     optional: true                       // gracefully skip if missing
 *   }
 *
 * Usage:
 *   <TourOverlay
 *     storageKey="evidence_rules_tour_seen_v1"
 *     bannerTitle="First time on Evidence Rules?"
 *     bannerCopy="Take the 30-second tour..."
 *     steps={EVIDENCE_RULES_STEPS}
 *   />
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { X, ArrowRight, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import { AUTO_START_TOUR_KEY, recordTourSeen } from "@/lib/tours";

export default function TourOverlay({
  storageKey,
  bannerTitle = "First time here?",
  bannerCopy = "Take the 60-second tour — we'll walk you through this page.",
  steps = [],
  bannerTone = "emerald",       // "emerald" | "amber" | "indigo" | "violet"
  testIdPrefix = "tour",
}) {
  const [showBanner, setShowBanner] = useState(false);
  const [running, setRunning] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [anchorRect, setAnchorRect] = useState(null);
  // iter146 — track whether the tour has already been dismissed so we can
  // render a tiny "Replay tour" link in place of the banner.
  const [alreadySeen, setAlreadySeen] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    try {
      if (typeof window === "undefined") return;
      if (!storageKey) return;
      // iter146c — Auto-start signal from "View all tours" Watch button.
      // If sessionStorage matches this tour's key, skip the banner and
      // dive straight into the spotlight, then consume the signal.
      const armed = window.sessionStorage.getItem(AUTO_START_TOUR_KEY);
      if (armed && armed === storageKey) {
        window.sessionStorage.removeItem(AUTO_START_TOUR_KEY);
        window.localStorage.removeItem(storageKey);
        setAlreadySeen(false);
        setStepIdx(0);
        setRunning(true);
        return;
      }
      const seen = window.localStorage.getItem(storageKey);
      if (!seen) setShowBanner(true);
      else setAlreadySeen(true);
    } catch { /* localStorage blocked — silent */ }
  }, [storageKey]);

  const computeRect = useCallback(() => {
    const step = steps[stepIdx];
    if (!step?.selector) { setAnchorRect(null); return; }
    const el = document.querySelector(step.selector);
    if (!el) {
      if (step.optional) {
        setStepIdx((i) => Math.min(i + 1, steps.length - 1));
      } else {
        setAnchorRect(null);
      }
      return;
    }
    const r = el.getBoundingClientRect();
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    setAnchorRect({
      top: r.top, left: r.left, right: r.right, bottom: r.bottom,
      width: r.width, height: r.height,
    });
  }, [stepIdx, steps]);

  useEffect(() => {
    if (!running) return;
    const t = setTimeout(computeRect, 250);
    const onResize = () => computeRect();
    window.addEventListener("resize", onResize);
    return () => { clearTimeout(t); window.removeEventListener("resize", onResize); };
  }, [running, stepIdx, computeRect]);

  const start = () => { setShowBanner(false); setRunning(true); setStepIdx(0); };

  // iter146 — clear the seen flag and replay the tour from scratch.
  const replay = () => {
    try { if (storageKey) window.localStorage.removeItem(storageKey); } catch {}
    setAlreadySeen(false);
    setStepIdx(0);
    setRunning(true);
  };

  const dismiss = useCallback(() => {
    try { if (storageKey) window.localStorage.setItem(storageKey, "1"); } catch {}
    // iter146d — best-effort cross-device progress tracking. Resolved
    // promise drives no UI; failures are silent.
    try { if (storageKey) recordTourSeen(storageKey); } catch {}
    setShowBanner(false);
    setRunning(false);
    setAlreadySeen(true);
  }, [storageKey]);

  const next = () => {
    if (stepIdx >= steps.length - 1) { dismiss(); return; }
    setStepIdx(stepIdx + 1);
  };

  if (steps.length === 0) return null;

  const tone = TONES[bannerTone] || TONES.emerald;

  // iter146 — tour was previously dismissed; offer a tiny "Replay tour" pill
  // so admins can re-trigger this single tour without going to My Access.
  if (!showBanner && !running && alreadySeen) {
    return (
      <div className="mb-4 flex justify-end" data-testid={`${testIdPrefix}-replay-wrap`}>
        <button onClick={replay} data-testid={`${testIdPrefix}-replay`}
                title="Replay this tour"
                className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border bg-white ${tone.icon} hover:opacity-90`}
                style={{ borderColor: "currentColor", opacity: 0.7 }}>
          <ArrowsClockwise size={10} weight="bold" /> Replay tour
        </button>
      </div>
    );
  }

  if (!showBanner && !running) return null;

  if (showBanner && !running) {
    return (
      <div className={`${tone.bg} ${tone.border} border rounded-md p-3 mb-4 flex items-center gap-3`}
           data-testid={`${testIdPrefix}-banner`}>
        <Sparkle size={14} weight="fill" className={`${tone.icon} flex-shrink-0`} />
        <span className={`text-xs ${tone.text} flex-1`}>
          <span className="font-bold">{bannerTitle}</span> {bannerCopy}
        </span>
        <button onClick={start} data-testid={`${testIdPrefix}-start`}
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm ${tone.button} text-white hover:opacity-90`}>
          Start tour <ArrowRight size={11} weight="bold" />
        </button>
        <button onClick={dismiss} data-testid={`${testIdPrefix}-dismiss`}
                title="Don't show again"
                className={`${tone.icon} hover:opacity-90 p-1`}>
          <X size={12} weight="bold" />
        </button>
      </div>
    );
  }

  const step = steps[stepIdx];
  return (
    <div ref={overlayRef}
         className="fixed inset-0 z-[700] pointer-events-none"
         data-testid={`${testIdPrefix}-overlay`}>
      {anchorRect ? (
        <svg className="absolute inset-0 w-full h-full pointer-events-auto">
          <defs>
            <mask id={`${testIdPrefix}-hole`}>
              <rect width="100%" height="100%" fill="white" />
              <rect x={anchorRect.left - 6} y={anchorRect.top - 6}
                    width={anchorRect.width + 12} height={anchorRect.height + 12}
                    rx="6" ry="6" fill="black" />
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="rgba(15,23,42,0.55)" mask={`url(#${testIdPrefix}-hole)`} />
        </svg>
      ) : (
        <div className="absolute inset-0 bg-slate-900/55 pointer-events-auto" />
      )}

      <div className="absolute pointer-events-auto bg-white border border-slate-200 rounded-md shadow-xl max-w-[320px] p-4"
           data-testid={`${testIdPrefix}-step-${step.id}`}
           style={tooltipStyle(anchorRect, step.placement)}>
        <div className="flex items-center gap-1 mb-1">
          <span className={`text-[10px] font-bold uppercase tracking-wider ${tone.icon}`}>
            Tour · step {stepIdx + 1} of {steps.length}
          </span>
        </div>
        <h3 className="text-sm font-bold mb-1">{step.title}</h3>
        <p className="text-xs text-slate-700 leading-relaxed mb-3">{step.body}</p>
        <div className="flex items-center gap-2">
          <button onClick={dismiss} data-testid={`${testIdPrefix}-skip`}
                  className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800">
            Skip
          </button>
          <button onClick={next} data-testid={`${testIdPrefix}-next`}
                  className={`ml-auto inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${tone.button} text-white hover:opacity-90`}>
            {stepIdx >= steps.length - 1 ? "Got it" : "Next"} <ArrowRight size={10} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

const TONES = {
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", icon: "text-emerald-700", button: "bg-emerald-600" },
  amber:   { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-900",   icon: "text-amber-700",   button: "bg-amber-500"  },
  indigo:  { bg: "bg-indigo-50",  border: "border-indigo-200",  text: "text-indigo-900",  icon: "text-indigo-700",  button: "bg-indigo-600" },
  violet:  { bg: "bg-violet-50",  border: "border-violet-200",  text: "text-violet-900",  icon: "text-violet-700",  button: "bg-violet-600" },
};

function tooltipStyle(rect, placement) {
  if (!rect) {
    return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }
  const margin = 12;
  const card = { width: 320, height: 180 };
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  let top = rect.bottom + margin;
  let left = rect.left;
  if (placement === "above")          { top = rect.top - card.height - margin; }
  else if (placement === "right")     { top = rect.top; left = rect.right + margin; }
  else if (placement === "below-end") { top = rect.bottom + margin; left = rect.right - card.width; }
  left = Math.max(8, Math.min(left, vw - card.width - 8));
  top  = Math.max(8, Math.min(top,  vh - card.height - 8));
  return { top: `${top}px`, left: `${left}px` };
}
